import React, { useState } from 'react';
import classNames from 'classnames';
import {
  Settings,
  Tool,
  Tag,
  Image,
  Target,
  Calendar,
  Link,
  Layers,
  BookOpen,
  Zap,
  HelpCircle,
  LogOut,
  Globe,
} from 'react-feather';
import { routes } from 'constants/config';
import ActionList from 'ui/ActionList';
import Popover from 'ui/Popover';
import IconButton from 'ui/IconButton';
import { useSessionStorage, useFeatureFlags } from 'hooks';
import { removeAuthTokenFromLocalStorage } from 'utils/centralAuthToken';

const CogMenu = ({
  anonUniversityLoginUrl,
  meta,
  navbarCollapsed,
  pendoClassName,
}) => {
  const { currentUser, features, permissions, locale } = meta;
  const {
    canEditSettings,
    canEditOrViewSettings,
    canViewTags,
    canViewImageGallery,
    canViewTriggers,
    canCreateAssignments,
    canViewRelatedUrls,
    canCreateUsers,
  } = permissions;

  const [isOpen, setIsOpen] = useState(false);

  const isCurrentUserLearner = currentUser.role === 'learner';
  const isIframedInSeismic = useSessionStorage('iframedInSeismic');
  const hasUnificationFeatureFlag = useFeatureFlags(
    'readiness-enable-unification',
  );
  const hasthirdPartyTools = useFeatureFlags('learning-third-party-tool-links');
  const hasUnificationAndSeismic =
    hasUnificationFeatureFlag && isIframedInSeismic;
  const hasSeismicNoUnification =
    !hasUnificationFeatureFlag && isIframedInSeismic;
  const onNonProd =
    window.railsHost === 'staging-1.lessonly.engineering' ||
    'lessonly.test' ||
    'lessonlybeta.com';

  if (!canEditSettings && hasUnificationFeatureFlag) {
    return <></>;
  }
  return (
    <li className="ml-4 mr-4">
      <Popover
        activator={
          <IconButton
            onClick={() => setIsOpen(!isOpen)}
            aria-label={t('settings.title')}
            className={classNames('CogMenu-IconButton bg-white', {
              [pendoClassName]: pendoClassName,
            })}
            disableHover
          >
            <Settings size={20} />
          </IconButton>
        }
        align={locale.direction === 'rtl' ? 'left' : 'right'}
        className={classNames('CogMenu', { mobileDisplay: navbarCollapsed })}
        visible={isOpen}
        onClose={() => setIsOpen(false)}
        onSelect={() => setIsOpen(false)}
      >
        <ActionList
          items={[
            canEditOrViewSettings && {
              content: t('settings.title'),
              FeatherIcon: Tool,
              url: routes.SETTINGS.INDEX,
            },
            canViewTags && {
              content: t('tags.title'),
              FeatherIcon: Tag,
              url: routes.TAGS,
            },
            canViewImageGallery && {
              content: t('image_library.title'),
              FeatherIcon: Image,
              url: routes.IMAGES,
            },
            canViewTriggers && {
              content: t('triggers.title'),
              FeatherIcon: Target,
              url: routes.TRIGGERS.INDEX,
            },
            canCreateAssignments && {
              content: t('scheduled_assignments.title'),
              FeatherIcon: Calendar,
              url: routes.SCHEDULED_ASSIGNMENTS,
            },
            features.related_urls &&
              canViewRelatedUrls && {
                content: t('related_urls.title'),
                FeatherIcon: Link,
                url: routes.RELATED_URLS,
              },
            features.web_widget &&
              canViewRelatedUrls && {
                content: t('web_widget.title'),
                FeatherIcon: Link,
                url: routes.WEB_WIDGETS,
              },
            canCreateUsers &&
              !hasUnificationFeatureFlag && {
                content: t('user_imports.title'),
                FeatherIcon: Layers,
                url: routes.NEW_BULK_IMPORT,
              },
            anonUniversityLoginUrl &&
            hasthirdPartyTools &&
            !isCurrentUserLearner &&
            !hasSeismicNoUnification
              ? !hasUnificationAndSeismic && {
                  content: t('common.university_login.title_rebrand'),
                  FeatherIcon: BookOpen,
                  url: onNonProd
                    ? 'https://seismicdev.thoughtindustries.com/learn/'
                    : 'https://university.seismic.com/learn',
                  openInNewTab: true,
                }
              : !isCurrentUserLearner && {
                  content: t('university_anonymous_login.title'),
                  FeatherIcon: BookOpen,
                  url: anonUniversityLoginUrl,
                  openInNewTab: true,
                },
            !isCurrentUserLearner && hasthirdPartyTools
              ? {
                  content: t('common.product_board.title_rebrand'),
                  FeatherIcon: Zap,
                  url: 'https://portal.productboard.com/xht2xgpp23ecjbwsuzhb3nie',
                  openInNewTab: true,
                }
              : !isCurrentUserLearner && {
                  content: t('product_board.title'),
                  FeatherIcon: Zap,
                  url: 'https://portal.productboard.com/xht2xgpp23ecjbwsuzhb3nie',
                  openInNewTab: true,
                },
            !isCurrentUserLearner &&
              !hasUnificationAndSeismic &&
              hasthirdPartyTools &&
              !hasSeismicNoUnification && {
                content: t('common.community.title_rebrand'),
                FeatherIcon: Globe,
                url: onNonProd
                  ? 'http://community-dev.seismic.com/community'
                  : 'http://community.seismic.com/',
                openInNewTab: true,
              },
            hasUnificationFeatureFlag &&
            !isIframedInSeismic &&
            !isCurrentUserLearner &&
            hasthirdPartyTools
              ? !hasUnificationAndSeismic && {
                  content: t('common.help_center.title_rebrand'),
                  FeatherIcon: HelpCircle,
                  url: onNonProd
                    ? 'https://seismic-dev.zoominsoftware.io/'
                    : 'https://docs.seismic.com',
                  openInNewTab: true,
                }
              : !hasUnificationAndSeismic &&
                !isCurrentUserLearner && {
                  content: t('help_center.title'),
                  FeatherIcon: HelpCircle,
                  url: 'https://intercom.help/lessonly/en/',
                  openInNewTab: true,
                },
            !isIframedInSeismic && {
              content: t('sessions.end', { name: currentUser.name }),
              FeatherIcon: LogOut,
              onClick: () => {
                removeAuthTokenFromLocalStorage();
                window.location.assign(routes.SIGNOUT);
              },
            },
          ]}
        />
      </Popover>
    </li>
  );
};

export default props => <CogMenu {...props} />;
